import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useNotificationPreferences } from "@/hooks/useNotificationPreferences";

interface UserNotificationSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserNotificationSettingsModal: React.FC<UserNotificationSettingsModalProps> = ({
  isOpen,
  onClose
}) => {
  const { 
    preferences, 
    isLoading, 
    isSaving, 
    updatePreference, 
    sendTestNotification 
  } = useNotificationPreferences();

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Notification Settings</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center p-8">Loading...</div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Notification Settings</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Email Notifications */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="email-notifications" className="text-base font-medium">
                Email Notifications
              </Label>
              <Switch
                id="email-notifications"
                checked={preferences.email_notifications_enabled}
                onCheckedChange={(checked) => updatePreference('email_notifications_enabled', checked)}
                disabled={isSaving}
              />
            </div>
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => sendTestNotification('email')}
                disabled={isSaving || !preferences.email_notifications_enabled}
              >
                Send Test Email
              </Button>
            </div>
          </div>

          {/* SMS Notifications */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="sms-notifications" className="text-base font-medium">
                SMS Notifications
              </Label>
              <Switch
                id="sms-notifications"
                checked={preferences.sms_notifications_enabled}
                onCheckedChange={(checked) => updatePreference('sms_notifications_enabled', checked)}
                disabled={isSaving}
              />
            </div>
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => sendTestNotification('sms')}
                disabled={isSaving || !preferences.sms_notifications_enabled}
              >
                Send Test SMS
              </Button>
            </div>
          </div>

          {/* Follow Notifications */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="follow-notifications" className="text-base font-medium">
                New Follower Notifications
              </Label>
              <Switch
                id="follow-notifications"
                checked={preferences.follow_notifications_enabled}
                onCheckedChange={(checked) => updatePreference('follow_notifications_enabled', checked)}
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Profile View Notifications */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="profile-view-notifications" className="text-base font-medium">
                Profile View Notifications
              </Label>
              <Switch
                id="profile-view-notifications"
                checked={preferences.profile_view_notifications_enabled}
                onCheckedChange={(checked) => updatePreference('profile_view_notifications_enabled', checked)}
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={onClose}
              disabled={isSaving}
            >
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserNotificationSettingsModal;